import React from 'react';
import PropTypes from 'prop-types';
import {css} from 'aphrodite/no-important';
// Core
import CoreMetrics, {createExpandableMediaQueryStylesheet} from '../core/Metrics';
import {LabelMediumText} from '../core/Type';
import Glyph from '../core/Glyph';

export default class Button extends React.Component {
    static Style = {
        Primary: "primary", // Default style
        Secondary: "secondary",
        Text: "text",
        // We have throughout the product buttons that look like links and
        // links that look like buttons. This resuable button component allows
        // you to make a link or a button and style it as either.
        // TODO(diedra): Add `url` and `onClick` props that determine if the
        // element has a `button` or an `a` tag.
        Link: "link",
    }

    static Variant = {
        Default: "default", // Default variant
        Toggle: "toggle",
        Dropdown: "dropdown",
    }

    state = {
        // Whether the dropdown menu associated with this button is
        // open or not.
        // If this button is not a dropdown variant, this state variable
        // is never used.
        // TODO(diedra): Split dropdowns out into a separate component
        // that uses this Button component for the styling.
        dropdownOpen: false,
    }

    handleClick = (event) => {
        // This is a bit of a hack that allows us to override the focus
        // effect when a user clicks on a button with a mouse.
        // TODO(diedra): When we add onClick prop functionality, make
        // sure that activating a button with the "Enter" or spacebar
        // keys doesn't blur the button. To do this, we'll need to
        // have a separate event that handles keyboard button
        // activation.
        this.button.blur();

        if (this.props.variant === Button.Variant.Dropdown) {
            this.setState({dropdownOpen: !this.state.dropdownOpen});
        }
    }

    render() {
        const styles = createExpandableMediaQueryStylesheet({
            button: {
                display: "flex",
                alignItems: "center",
                padding: 0,
                height: CoreMetrics.ElementHeight.Small(),
                backgroundColor: CoreMetrics.Color.None,
                color: CoreMetrics.Color.Control.Default,
                border: "none",
                outline: "none",
            },
            withBackground: {
                paddingTop: 0,
                paddingLeft: CoreMetrics.ElementPadding.Medium(),
                paddingRight: CoreMetrics.ElementPadding.Medium(),
                borderRadius: CoreMetrics.CornerRadius,
            },
            withoutBackground: {
                color: CoreMetrics.Color.Control.Default,

                ":hover": {
                    color: CoreMetrics.Color.Control.Hover,
                },
                ":focus": {
                    boxShadow: `0 0 0 2px ${CoreMetrics.Color.Control.Default}`,
                },
                ":active": {
                    color: CoreMetrics.Color.Control.Active,
                    boxShadow: "none",
                },
            },
            primary: {
                backgroundColor: CoreMetrics.Color.Control.Default,
                color: CoreMetrics.Color.Neutral.White,
                border: "none",

                ":hover": {
                    backgroundColor: CoreMetrics.Color.Control.Hover,
                },
                ":focus": {
                    // This is not the best way to handle the focus
                    // outline because the gap between the button and
                    // the outline will always be white, even if the
                    // button is on a non-white background.
                    // TODO(diedra): Try using another element inside
                    // the button that has the blue background while the
                    // outer button has the boxshadow on focus.
                    boxShadow: `0 0 0 1px ${CoreMetrics.Color.Neutral.White},
                        0 0 0 2px ${CoreMetrics.Color.Control.Default}`,
                },
                ":active": {
                    backgroundColor: CoreMetrics.Color.Control.Active,
                    boxShadow: "none",
                },
            },
            secondary: {
                backgroundColor: CoreMetrics.Color.None,
                color: CoreMetrics.Color.Control.Default,
                boxShadow: `0 0 0 1px ${CoreMetrics.Color.Control.Default},
                    0 0 0 2px ${CoreMetrics.Color.Neutral.White}`,

                ":hover": {
                    backgroundColor: CoreMetrics.HexToRgba(
                        CoreMetrics.Color.Control.Hover, 0.1),
                },
                ":focus": {
                    boxShadow: `0 0 0 2px ${CoreMetrics.Color.Control.Default}`,
                },
                ":active": {
                    backgroundColor: CoreMetrics.HexToRgba(
                        CoreMetrics.Color.Control.Active, 0.2),
                    color: CoreMetrics.Color.Control.Active,
                    boxShadow: `0 0 0 1px ${CoreMetrics.Color.Control.Default},
                        0 0 0 2px ${CoreMetrics.Color.Neutral.White}`,
                },
            },
            link: {
                margin: 0,
                borderRadius: 0,

                ":hover": {
                    borderRadius: 0,
                    boxShadow: `0 0.5px 0 0
                        ${CoreMetrics.Color.Control.Hover}`,
                },
                ":focus": {
                    borderRadius: CoreMetrics.CornerRadius,
                },
                ":active": {
                    // We reset the borderRadius to 0 here because the
                    // active state triggers the focus state, which has
                    // a curved borderRadius. We blur the button
                    // immediately on click, but the shadow still
                    // appears curved briefly without this override.
                    borderRadius: 0,
                    boxShadow: `0 0.5px 0 0
                        ${CoreMetrics.Color.Control.Active}`,
                },
            },
            label: {
                color: "inherit",
                marginLeft: "auto",
                marginRight: "auto",
            },
            labelWithoutBackground: {
                color: "inherit",
            },
            iconWithoutText: {
                marginRight: CoreMetrics.ElementPadding.XXSmall(),
                ":hover": {
                    cursor: "pointer",
                },
            },
            dropdown: {
                marginLeft: CoreMetrics.ElementPadding.XXSmall(),
                // TODO(diedra): Pull this transition length out into a shared
                // metric.
                transition: "transform 0.2s",
            },
            dropdownOpen: {
                transform: "rotateX(180deg)",
            },
            sideChevron: {
                transform: "rotate(-90deg)",
            },
        });

        const {style, variant} = this.props;

        let combinedStyles = [styles.button];
        if (style === Button.Style.Primary) {
            combinedStyles.push(styles.withBackground);
            combinedStyles.push(styles.primary);
        } else if (style === Button.Style.Secondary) {
            combinedStyles.push(styles.withBackground);
            combinedStyles.push(styles.secondary);
        } else if (style === Button.Style.Text) {
            combinedStyles.push(styles.withoutBackground);
        } else if (style === Button.Style.Link) {
            combinedStyles.push(styles.withoutBackground);
            combinedStyles.push(styles.link);
        }

        let {title} = this.props;
        if (!title && variant === Button.Variant.Dropdown) {
            title = this.state.dropdownOpen ? "Less" : "More";
        }
        let titleElement = null;
        if (title !== "") {
            const labelStyle = style === Button.Style.Primary
                ? styles.label
                : styles.labelWithoutBackground;
            titleElement = <LabelMediumText styles={[labelStyle]}>
                {title}
            </LabelMediumText>;
        }

        const icon = this.props.icon;
        let iconElement = null;
        if (icon != null) {
            const iconStyles = [];
            if (title !== "") {
                iconStyles.push(styles.iconWithoutText);
            }
            iconElement = <Glyph
                styles={iconStyles}
                asset={icon}
                size={Glyph.Size.Medium}
                fillColor={"currentColor"}
            />;
        }

        let dropdownIconElement = null;
        if (variant === Button.Variant.Dropdown ||
            style === Button.Style.Link
        ) {
            const asset = style === Button.Style.Link
                ? "assets/12x12-chevron.svg"
                : "assets/12x12-dropdown.svg";

            const dropdownStyles = [styles.dropdown];
            if (variant !== Button.Variant.Dropdown) {
                dropdownStyles.push(styles.sideChevron);
            } else if (this.state.dropdownOpen) {
                dropdownStyles.push(styles.dropdownOpen);
            }

            dropdownIconElement = <Glyph
                styles={dropdownStyles}
                asset={asset}
                size={Glyph.Size.XSmall}
                fillColor="currentColor"
            />;
        }

        if (this.props.styles != null) {
            combinedStyles = combinedStyles.concat(this.props.styles);
        }
        return (
            <button
                ref={(node) => this.button = node}
                className={css(combinedStyles)}
                onClick={(event) => this.handleClick(event)}
            >
                {iconElement}
                {titleElement}
                {dropdownIconElement}
            </button>
        );
    }
}
Button.defaultProps = {
    title: "",
    icon: null,
    style: Button.Style.Primary,
    variant: Button.Variant.Default,
    styles: [],
};
Button.propTypes = {
    title: PropTypes.string,
    icon: PropTypes.string,
    style: PropTypes.string,
    variant: PropTypes.string,
    styles: PropTypes.array,
};



// WEBPACK FOOTER //
// ./src/controls/Button.jsx